import { Head, router } from "@inertiajs/react"
import { MoreVertical, Pencil, Plus } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { BadgeAwardModal } from "@/components/badges/badge-award-modal"
import { BadgeGrid } from "@/components/badges/badge-grid"
import { BadgeTile } from "@/components/badges/badge-tile"
import { CustomBadgeFormDialog } from "@/components/badges/custom-badge-form-dialog"
import { MilestoneBadgeFormDialog } from "@/components/badges/milestone-badge-form-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useMenuDialogHandoff } from "@/hooks/use-menu-dialog-handoff"
import AppLayout from "@/layouts/app-layout"
import { familyFor, tierFor } from "@/lib/badge-tiers"
import { badgePath } from "@/routes"
import type {
  BadgesIndexProps,
  CustomBadgeTile,
  MilestoneCatalogTile,
} from "@/types/badges"

/**
 * OKS-50 — Brand admin badges page (`/badges`).
 *
 * Two-tab layout (Catalogue + Awards). Catalogue shows the platform
 * milestones (read-only) + the brand's custom badges (managed via
 * `<CustomBadgeFormDialog>`). Awards tab shows the recent log of
 * BadgeAward rows for the brand.
 *
 * Brand isolation: `milestones[].earner_count` + `custom_badges[]`
 * + `awards[]` are server-scoped to `Current.brand`. No client-side
 * filter widens the surface.
 */
export default function BrandBadgesIndex({
  milestones,
  custom_badges,
  awards,
  awards_pagination,
  crew_candidates,
  can_manage,
  available_icons,
  available_colors,
}: BadgesIndexProps) {
  const { t, i18n } = useTranslation()

  return (
    <AppLayout>
      <Head title={t("badges.title")} />

      <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">{t("badges.title")}</h1>
            <p className="text-muted-foreground text-sm">
              {t("badges.subtitle")}
            </p>
          </div>
          {can_manage && (
            <CustomBadgeFormDialog
              availableIcons={available_icons}
              availableColors={available_colors}
              trigger={
                <Button data-testid="create-custom-badge-cta">
                  <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                  {t("badges.actions.create")}
                </Button>
              }
            />
          )}
        </header>

        <Tabs defaultValue="catalogue">
          <TabsList>
            <TabsTrigger value="catalogue">
              {t("badges.tabs.catalogue")}
            </TabsTrigger>
            <TabsTrigger value="awards">{t("badges.tabs.awards")}</TabsTrigger>
          </TabsList>

          <TabsContent value="catalogue" className="flex flex-col gap-6">
            <section>
              <h2 className="text-muted-foreground mb-3 text-sm font-semibold uppercase">
                {t("badges.sections.milestones")}
              </h2>
              <BadgeGrid>
                {milestones.map((m) => (
                  <MilestoneTile
                    key={m.identifier}
                    tile={m}
                    canManage={can_manage}
                  />
                ))}
              </BadgeGrid>
            </section>

            <section>
              <h2 className="text-muted-foreground mb-3 text-sm font-semibold uppercase">
                {t("badges.sections.custom")}
              </h2>
              {custom_badges.length === 0 ? (
                <div className="bg-muted/40 text-muted-foreground flex flex-col items-center gap-4 rounded-lg border p-6 text-center text-sm">
                  <p>{t("badges.empty.brand_catalogue")}</p>
                  {/* OKS-653 — in-card create CTA for managers. Rendered as a
                      second self-contained <CustomBadgeFormDialog> instance
                      (each owns its own open state) so the empty state has a
                      working create path without lifting dialog state or
                      touching the header CTA. Non-managers get invite copy
                      only — no dead/disabled button. */}
                  {can_manage && (
                    <CustomBadgeFormDialog
                      availableIcons={available_icons}
                      availableColors={available_colors}
                      trigger={
                        <Button
                          variant="outline"
                          data-testid="create-custom-badge-cta-empty"
                        >
                          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                          {t("badges.actions.create")}
                        </Button>
                      }
                    />
                  )}
                </div>
              ) : (
                <BadgeGrid>
                  {custom_badges.map((b) => (
                    <CustomBadgeCard
                      key={b.id}
                      tile={b}
                      candidates={crew_candidates}
                      canManage={can_manage}
                      availableIcons={available_icons}
                      availableColors={available_colors}
                    />
                  ))}
                </BadgeGrid>
              )}
            </section>
          </TabsContent>

          <TabsContent value="awards">
            {awards.length === 0 ? (
              <div className="bg-muted/40 text-muted-foreground rounded-lg border p-6 text-center text-sm">
                {t("badges.empty.brand_awards")}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Creator</TableHead>
                    <TableHead>Badge</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {awards.map((award) => (
                    <TableRow
                      key={award.id}
                      data-testid={`award-row-${award.id}`}
                    >
                      <TableCell>{award.creator.name}</TableCell>
                      <TableCell>
                        {award.badge?.name ??
                          (award.milestone_identifier
                            ? t(
                                `badges.milestones.${familyFor(award.milestone_identifier)}.name`,
                              )
                            : "—")}
                      </TableCell>
                      <TableCell>
                        {award.source === "system_milestone"
                          ? "Auto"
                          : `Awarded by ${award.awarded_by_name ?? "—"}`}
                      </TableCell>
                      <TableCell>
                        {new Date(award.awarded_at).toLocaleDateString(
                          i18n.language,
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          },
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {awards_pagination.total > awards_pagination.per_page && (
              <p className="text-muted-foreground mt-3 text-xs">
                Page {awards_pagination.page} of{" "}
                {Math.ceil(
                  awards_pagination.total / awards_pagination.per_page,
                )}
              </p>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </AppLayout>
  )
}

function MilestoneTile({
  tile,
  canManage,
}: {
  tile: MilestoneCatalogTile
  canManage: boolean
}) {
  const { t } = useTranslation()
  const name = t(`badges.milestones.${tile.i18n_key}.name`)
  const description = t(`badges.milestones.${tile.i18n_key}.description`)

  return (
    // OKS-976 — `data-milestone-identifier` keys each tile to its family so
    // coverage can assert the rendered identifier SET against
    // `BadgeCatalog::FAMILY_IDENTIFIERS` rather than zipping medallions
    // against catalogue order. Order-independent by construction: a future
    // sort on this grid cannot silently pair one family's medallion with
    // another family's expected tier.
    <div
      className="flex flex-col gap-2"
      data-milestone-identifier={tile.identifier}
    >
      <BadgeTile
        // OKS-573 — render the brand override frame-less when present,
        // else the shipped Lucide medallion.
        imageUrl={tile.image_url}
        icon={tile.icon}
        accentColor={tile.accent_color}
        name={name}
        hint={description}
        // OKS-976 — the milestone LADDER decides prestige, not the accent
        // colour. Without this prop `BadgeTile` falls through to its
        // `tierFor({kind: "custom", accentColor})` default, whose `kind` is
        // hardcoded to the custom path and therefore cannot ever reach the
        // ladder — so 14 of the 18 shipped families rendered a tier that
        // disagreed with the one creators see on their own surfaces.
        //
        // `accentColor` is deliberately NOT passed: `kind` is the static
        // literal "milestone", so `tierFor` provably never reads an accent
        // here, and handing it one would restate in miniature the very
        // confusion this fixes — the accent appearing to have a say in
        // milestone prestige. Unknown identifiers still fall back to the
        // ladder's bronze default, exactly as the creator surfaces do.
        tier={tierFor({ kind: "milestone", identifier: tile.identifier })}
        // OKS-937 — NO state word here. `earnedAt` below is a styling
        // sentinel, not a fact: the brand admin has earned nothing, so
        // announcing "earned" would state a falsehood. The state word
        // keys off the SURFACE, never off `earnedAt`.
        ariaLabel={t("badges.aria.tile_catalogue", { name, description })}
        earnedAt="" // catalogue tile uses earned-style colour even though the brand admin hasn't "earned" it
      />
      <p className="text-muted-foreground text-center text-xs">
        {tile.earner_count === 1
          ? "1 creator earned this"
          : `${tile.earner_count} creators earned this`}
      </p>
      {/* OKS-573 — Customize affordance (image-only edit dialog). Milestone
          name/meaning stay platform-defined; only the image is overridable. */}
      {canManage && (
        <div className="flex items-center justify-center">
          <MilestoneBadgeFormDialog
            tile={tile}
            name={name}
            description={description}
            trigger={
              <Button
                size="sm"
                variant="ghost"
                className="min-h-11 text-xs sm:min-h-0"
                aria-label={t("badges.milestones.customize_aria", { name })}
                data-testid={`customize-milestone-${tile.identifier}`}
              >
                <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
                {t("badges.milestones.customize")}
              </Button>
            }
          />
        </div>
      )}
    </div>
  )
}

function CustomBadgeCard({
  tile,
  candidates,
  canManage,
  availableIcons,
  availableColors,
}: {
  tile: CustomBadgeTile
  candidates: BadgesIndexProps["crew_candidates"]
  canManage: boolean
  availableIcons: string[]
  availableColors: string[]
}) {
  const { t } = useTranslation()
  const [editOpen, setEditOpen] = useState(false)
  const { finalFocus, yieldFocusToDialog } = useMenuDialogHandoff()

  const onDelete = () => {
    if (
      window.confirm(
        t("badges.dialog.delete_confirm", { count: tile.earner_count }),
      )
    ) {
      router.delete(badgePath(tile.id), { preserveScroll: true })
    }
  }

  const candidatesForBadge = candidates.map((c) => ({
    membership_id: c.membership_id,
    name: c.name,
    handle: c.handle,
    already_earned: c.earned_badge_ids.includes(tile.id),
  }))

  return (
    <div className="flex flex-col gap-2">
      <BadgeTile
        variant="image"
        imageUrl={tile.image_url}
        icon={tile.icon}
        accentColor={tile.accent_color}
        name={tile.name}
        hint={tile.description}
        // OKS-937 — no state word (see MilestoneTile). `description` is
        // non-optional on `CustomBadgeTile` and DB-constrained non-blank, so
        // there is no empty-description branch to guard.
        ariaLabel={t("badges.aria.tile_catalogue", {
          name: tile.name,
          description: tile.description,
        })}
        earnedAt={new Date().toISOString()}
      />
      <p className="text-muted-foreground text-center text-xs">
        {tile.earner_count === 1
          ? "1 creator earned this"
          : `${tile.earner_count} creators earned this`}
      </p>
      {canManage && (
        <div className="flex items-center justify-center gap-2">
          <BadgeAwardModal
            badge={tile}
            candidates={candidatesForBadge}
            trigger={
              <Button size="sm" data-testid={`award-cta-${tile.id}`}>
                {t("badges.actions.award")}
              </Button>
            }
          />
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={t("badges.actions.menu_label", {
                    name: tile.name,
                  })}
                  data-testid={`badge-row-actions-${tile.id}`}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              }
            />
            <DropdownMenuContent align="end" finalFocus={finalFocus}>
              <DropdownMenuItem
                data-testid={`edit-badge-item-${tile.id}`}
                onClick={() => {
                  yieldFocusToDialog()
                  setEditOpen(true)
                }}
              >
                {t("badges.actions.edit")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDelete} className="text-destructive">
                {t("badges.actions.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <CustomBadgeFormDialog
            mode="edit"
            badge={tile}
            availableIcons={availableIcons}
            availableColors={availableColors}
            open={editOpen}
            onOpenChange={setEditOpen}
            dialogTestId={`edit-badge-dialog-${tile.id}`}
          />
        </div>
      )}
    </div>
  )
}
